import React, { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { AppContext } from '../../context/AppContext';
import { SistemaPuntuacion } from '../../models/types';
import { SistemaPuntuacionServiceFirestore } from '../../services/SistemaPuntuacionServiceFirestore';
import { FaSave, FaArrowLeft, FaPlus, FaTrash } from '../../components/Icons';

const Container = styled.div`
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  color: #1a1a2e;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
`;

const Button = styled.button`
  background-color: #0f3460;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  
  &:hover {
    background-color: #e94560;
  }
  
  svg {
    margin-right: 8px;
  }
`;

const BackButton = styled(Button)`
  background-color: #6c757d;
  
  &:hover {
    background-color: #5a6268;
  }
`;

const Form = styled.form`
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 15px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  color: #1a1a2e;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
`;

const Card = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
`;

const CardTitle = styled.h2`
  color: #0f3460;
  margin-bottom: 15px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  background-color: #0f3460;
  color: white;
  text-align: left;
  padding: 12px 15px;
`;

const Td = styled.td`
  padding: 12px 15px;
  border-bottom: 1px solid #ddd;
`;

const ActionButton = styled.button`
  background-color: transparent;
  border: none;
  cursor: pointer;
  margin-right: 10px;
  color: #0f3460;
  
  &:hover {
    color: #e94560;
  }
`;

const PuntuacionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, loadData } = useContext(AppContext);
  const [sistema, setSistema] = useState<SistemaPuntuacion | null>(null);
  const [formData, setFormData] = useState({
    nombre: '',
    vueltaRapida: 1,
    polePosition: 2,
    mejorConsistencia: 2,
    segundaMejorConsistencia: 1
  });
  const [puntosPosiciones, setPuntosPosiciones] = useState<number[]>([]);
  
  // Cargar datos del sistema de puntuación
  useEffect(() => {
    if (id === 'new') {
      // Nuevo sistema
      setSistema(null);
      setFormData({
        nombre: '',
        vueltaRapida: 1,
        polePosition: 2,
        mejorConsistencia: 2,
        segundaMejorConsistencia: 1
      });
      // Inicializar puntos por posición (24, 23, 22, ... y 0 para posiciones 25-50)
      const puntosIniciales = Array.from({ length: 50 }, (_, i) => {
        // Para las primeras 24 posiciones, usar el sistema clásico (24, 23, 22, ...)
        if (i < 24) {
          return 24 - i;
        } else {
          // Para posiciones 25-50, asignar 0 puntos por defecto
          return 0;
        }
      });
      setPuntosPosiciones(puntosIniciales);
    } else {
      // Sistema existente
      const sistemaExistente = data.sistemasPuntuacion.find(s => s.id === id);
      if (sistemaExistente) {
        setSistema(sistemaExistente);
        setFormData({
          nombre: sistemaExistente.nombre,
          vueltaRapida: sistemaExistente.puntosAdicionales.vueltaRapida,
          polePosition: sistemaExistente.puntosAdicionales.polePosition,
          mejorConsistencia: sistemaExistente.puntosAdicionales.mejorConsistencia,
          segundaMejorConsistencia: sistemaExistente.puntosAdicionales.segundaMejorConsistencia
        });
        setPuntosPosiciones([...sistemaExistente.puntosPorPosicion]);
      }
    }
  }, [id, data]);
  
  // Manejar cambios en el formulario principal
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Manejar cambios en los puntos por posición
  const handlePuntosChange = (index: number, value: number) => {
    const newPuntos = [...puntosPosiciones];
    newPuntos[index] = value;
    setPuntosPosiciones(newPuntos);
  };
  
  // Guardar sistema de puntuación
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const sistemaData = {
      nombre: formData.nombre,
      puntosPorPosicion: puntosPosiciones,
      puntosAdicionales: {
        vueltaRapida: parseInt(formData.vueltaRapida.toString()),
        polePosition: parseInt(formData.polePosition.toString()),
        mejorConsistencia: parseInt(formData.mejorConsistencia.toString()),
        segundaMejorConsistencia: parseInt(formData.segundaMejorConsistencia.toString())
      }
    };
    
    if (id === 'new') {
      // Crear nuevo sistema en Firestore
      await SistemaPuntuacionServiceFirestore.create(sistemaData);
    } else if (sistema) {
      // Actualizar sistema existente en Firestore
      await SistemaPuntuacionServiceFirestore.update(sistema.id, sistemaData);
      // Si tienes lógica de recalcular puntos, deberías migrarla a Firestore también
    }
    await loadData();
    navigate('/puntuaciones');
  };
  
  return (
    <Container>
      <Header>
        <Title>{id === 'new' ? 'Nuevo Sistema de Puntuación' : `Editar Sistema: ${formData.nombre}`}</Title>
        <ButtonGroup>
          <BackButton onClick={() => navigate('/puntuaciones')}>
            <FaArrowLeft /> Volver
          </BackButton>
        </ButtonGroup>
      </Header>
      
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="nombre">Nombre del Sistema</Label>
          <Input
            type="text"
            id="nombre"
            name="nombre"
            value={formData.nombre}
            onChange={handleChange}
            required
          />
        </FormGroup>
        
        <Card>
          <CardTitle>Puntos Adicionales</CardTitle>
          
          <FormGroup>
            <Label htmlFor="vueltaRapida">Puntos por Vuelta Rápida</Label>
            <Input
              type="number"
              id="vueltaRapida"
              name="vueltaRapida"
              min="0"
              value={formData.vueltaRapida}
              onChange={handleChange}
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="polePosition">Puntos por Pole Position</Label>
            <Input
              type="number"
              id="polePosition"
              name="polePosition"
              min="0"
              value={formData.polePosition}
              onChange={handleChange}
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="mejorConsistencia">Puntos por Mejor Consistencia</Label>
            <Input
              type="number"
              id="mejorConsistencia"
              name="mejorConsistencia"
              min="0"
              value={formData.mejorConsistencia}
              onChange={handleChange}
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="segundaMejorConsistencia">Puntos por Segunda Mejor Consistencia</Label>
            <Input
              type="number"
              id="segundaMejorConsistencia"
              name="segundaMejorConsistencia"
              min="0"
              value={formData.segundaMejorConsistencia}
              onChange={handleChange}
              required
            />
          </FormGroup>
        </Card>
        
        <Card>
          <CardTitle>Puntos por Posición</CardTitle>
          
          <div style={{ marginBottom: '15px', color: '#666', fontSize: '0.9rem' }}>
            Ahora puedes asignar puntos para hasta 50 participantes. Las posiciones 25-50 tienen 0 puntos por defecto.
          </div>
          
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            <div style={{ flex: '1', minWidth: '300px' }}>
              <h4 style={{ marginTop: 0, borderBottom: '1px solid #eee', paddingBottom: '8px' }}>Posiciones 1-25</h4>
              <Table>
                <thead>
                  <tr>
                    <Th>Posición</Th>
                    <Th>Puntos</Th>
                  </tr>
                </thead>
                <tbody>
                  {puntosPosiciones.slice(0, 25).map((puntos, index) => (
                    <tr key={index}>
                      <Td>{index + 1}º</Td>
                      <Td>
                        <Input
                          type="number"
                          min="0"
                          value={puntos}
                          onChange={(e) => handlePuntosChange(index, parseInt(e.target.value))}
                          style={{ width: '80px' }}
                        />
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            
            <div style={{ flex: '1', minWidth: '300px' }}>
              <h4 style={{ marginTop: 0, borderBottom: '1px solid #eee', paddingBottom: '8px' }}>Posiciones 26-50</h4>
              <Table>
                <thead>
                  <tr>
                    <Th>Posición</Th>
                    <Th>Puntos</Th>
                  </tr>
                </thead>
                <tbody>
                  {puntosPosiciones.slice(25, 50).map((puntos, index) => (
                    <tr key={index + 25}>
                      <Td>{index + 26}º</Td>
                      <Td>
                        <Input
                          type="number"
                          min="0"
                          value={puntos}
                          onChange={(e) => handlePuntosChange(index + 25, parseInt(e.target.value))}
                          style={{ width: '80px' }}
                        />
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        </Card>
        
        <Button type="submit">
          <FaSave /> Guardar Sistema de Puntuación
        </Button>
      </Form>
    </Container>
  );
};

export default PuntuacionDetail;
